<template>
    <div class="content">
        <div class="container-fluid">
            <transition name="fade" mode="out-in">
                <APILoading v-if="carrierArea === undefined || carrier === undefined"
                            :text="$t('CarrierArea.loadingCarrierArea')"
                            :color="$data.$colors.billing" key="loading"/>
                <ResourceNotFound v-else-if="carrierArea === null || carrier === null"
                                  key="not-found" category="billing"/>
                <div v-else key="carrier-area" id="carrier-area">
                    <router-link class="font-italic" :to="`/billing/carrier/${carrierArea.carrierId}`">
                        <i class="fa fa-chevron-circle-left"/>
                        {{ `${$t("CarrierArea.backToCarrier")} - ${carrierArea.carrier.name}` }}
                    </router-link>
                    <PageTitle :title="pageTitle" class="mt-2">
                        <template v-slot:left>
                            <i class="fas fa-globe fa-3x text-billing"/>
                        </template>
                    </PageTitle>
                    <div class="row">
                        <div v-if="!carrierArea.destinations.length" class="col-12 d-flex align-items-center alert alert-info">
                            <span class="fa fa-info-circle fa-2x mr-2"/>
                            <span>
                                {{ $t("CarrierArea.noCarrierDestinationYet") }}
                                <br/>
                                <a class="font-bold text-info" href="#" @click.prevent="$refs.CarrierAreaInfoCard.showCarrierAreaModal()">
                                    {{ $t("CarrierArea.youCanAddCarrierDestinationHere") }}
                                    <i class="fas fa-plus-circle"/>
                                </a>
                            </span>
                        </div>
                        <CarrierInfoCard @editCarrier="editCarrier" :carrier="carrier"/>
                        <CarrierAreaInfoCard @editCarrierArea="editCarrierArea" :carrier="carrier" :carrier-area="carrierArea"
                                             ref="CarrierAreaInfoCard"/>
                    </div>
                    <div class="row" v-if="carrierArea.destinations.length">
                        <div class="col-lg-12">
                            <div class="card card-tabs">
                                <div class="card-body">
                                    <ul class="nav nav-pills float-right" role="tablist">
                                        <li class="nav-item">
                                            <a class="nav-link show active" id="prices-tab" data-toggle="pill"
                                               href="#destinations" role="tab" aria-controls="destinations"
                                               aria-expanded="true" aria-selected="true">
                                                <i class="fa fa-euro mr-1"/>
                                                {{ $t("CarrierArea.prices") }}
                                                <span class="badge badge-light"> {{ carrierArea.destinations[0].prices.length }} </span>
                                            </a>
                                        </li>
                                    </ul>
                                    <h4 class="header-title m-b-30"> {{$t("CarrierArea.manage")}} </h4>
                                    <div class="tab-content">
                                        <div class="tab-pane fade show active" id="prices" role="tabpanel" aria-labelledby="prices-tab">
                                            <div class="row">
                                                <CarrierDestinationPriceList :can-edit-if-destination-has-area="true" :carrier="carrier"
                                                                            :carrier-destination="carrierArea.destinations[0]"
                                                                            @editCarrierArea="editCarrierArea"/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import APILoading from "../../../shared/API/APILoading";
import ResourceNotFound from "../../../shared/misc/ResourceNotFound";
import PageTitle from "../../../shared/misc/PageTitle";
import CarrierArea from "../../../../classes/Billing/Carrier/CarrierArea";
import Carrier from "../../../../classes/Billing/Carrier/Carrier";
import CarrierInfoCard from "../../../shared/Billing/Carrier/CarrierInfoCard";
import CarrierAreaInfoCard from "./CarrierAreaInfoCard";
import CarrierDestinationPriceList
    from "../../../shared/Billing/CarrierDestination/CarrierDestinationPriceList/CarrierDestinationPriceList";
import CarrierDestinationPrice from "../../../../classes/Billing/Carrier/CarrierDestinationPrice";

export default {
    name: "CarrierArea",
    components: { CarrierDestinationPriceList, CarrierAreaInfoCard, CarrierInfoCard, PageTitle, ResourceNotFound, APILoading },
    data() {
        return {
            carrierArea: undefined,
            carrier: undefined,
        };
    },
    async created() {
        await this.checkUserAuthentication();
        if (!isNaN(this.carrierAreaId) && this.carrierAreaId > 0) {
            await this.getCarrierArea();
        } else {
            this.carrierArea = null;
        }
    },
    methods: {
        ...mapActions("user", {
            checkUserAuthentication: "checkUserAuthentication",
        }),
        async getCarrierArea() {
            try {
                let response = await this.$diceAPI.getCarrierArea(this.carrierAreaId);
                this.carrierArea = new CarrierArea(response.data);
                response = await this.$diceAPI.getCarrier(this.carrierArea.carrierId);
                this.carrier = new Carrier(response.data);
            } catch (e) {
                if (e.response.status === 404) {
                    this.carrierArea = null;
                } else {
                    this.$displayError(e);
                }
            }
        },
        editCarrier(carrier) {
            this.carrier = new Carrier(carrier);
        },
        editCarrierArea(carrierArea) {
            this.carrierArea = new CarrierArea(carrierArea);
            const index = this.carrier.areas.findIndex(area => area.id === carrierArea.id);
            this.carrier.areas.splice(index, 1, carrierArea);
            for (const destination of this.carrier.destinations) {
                if (destination.carrierAreaId === carrierArea.id && carrierArea.destinations.find(d => d.id === destination.id) === undefined) {
                    destination.carrierAreaId = null;
                    destination.area = new CarrierArea();
                } else if (carrierArea.destinations.find(d => d.id === destination.id)) {
                    destination.carrierAreaId = carrierArea.id;
                    destination.area = new CarrierArea(carrierArea);
                }
            }
        },
        addCarrierDestinationPrice(carrierDestinationPrice) {
            this.carrierArea.destinations[0].prices.push(new CarrierDestinationPrice(carrierDestinationPrice));
        },
        editCarrierDestinationPrice(carrierDestinationPrice) {
            const index = this.carrierArea.destinations[0].prices.findIndex(price => price.id === carrierDestinationPrice.id);
            this.carrierArea.destinations[0].prices.splice(index, 1, new CarrierDestinationPrice(carrierDestinationPrice));
        },
        deleteCarrierDestinationPrice(carrierDestinationPrice) {
            const index = this.carrierArea.destinations[0].prices.findIndex(price => price.id === carrierDestinationPrice.id);
            this.carrierArea.destinations[0].prices.splice(index, 1);
        },
    },
    computed: {
        pageTitle() {
            const rightLabel = `<span class='pull-right'>
                                    ${this.carrierArea.carrier.name}
                                    <i class='fas fa-globe text-billing'></i>
                                    ${this.carrierArea.name}
                                </span>`;
            return `${this.$t("CarrierArea.carrierAreaCard")} ${rightLabel}`;
        },
        carrierAreaId() {
            return parseInt(this.$route.params.id);
        },
    },
};
</script>

<style scoped>

</style>